import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import getStoredUser from '../utils/getStoredUser';
import { PASSWORD_POLICY_TEXT, getPasswordPolicyError } from '../utils/passwordPolicy';
import { formatDayInZone } from '../utils/timezone';
import api, { authAPI } from '../services/api';
import { completeJourneyStep } from '../utils/journeyProgress';
import NotificationSettings from './NotificationSettings';

// Render arbitrary user-supplied text as a React child WITHOUT crashing on
// objects/arrays/null. The whole point of the VersionInfo crash fix is "do
// not trust the shape" — finish the job at the leaf nodes.
const safeText = (v) => {
  if (v == null) return '';
  const t = typeof v;
  if (t === 'string' || t === 'number' || t === 'boolean') return String(v);
  try { return JSON.stringify(v); } catch { return ''; }
};

const VersionInfo = () => {
  // status: 'loading' | 'ok' | 'error'
  const [verState, setVerState] = useState({ status: 'loading', data: null });

  useEffect(() => {
    let cancelled = false;
    // Static-only fetch with explicit no-store + r.ok guard. We deliberately
    // do NOT fall back to /api/version because the global axios interceptor
    // redirects to /login on ANY 401, which would kick a coach with an
    // expired token off the app simply for opening Settings.
    fetch('/version.json?t=' + Date.now(), { cache: 'no-store' })
      .then(r => {
        if (!r.ok) throw new Error('not ok ' + r.status);
        const ct = (r.headers && r.headers.get && r.headers.get('content-type')) || '';
        if (!/json/i.test(ct)) throw new Error('not json: ' + ct);
        return r.json();
      })
      .then(d => { if (!cancelled) setVerState({ status: 'ok', data: d }); })
      .catch(() => { if (!cancelled) setVerState({ status: 'error', data: null }); });
    return () => { cancelled = true; };
  }, []);

  if (verState.status === 'loading') {
    return <p style={{ color: 'var(--text-dim)', fontSize: '11px' }}>Loading...</p>;
  }
  if (verState.status === 'error' || !verState.data) {
    return <p style={{ color: 'var(--text-dim)', fontSize: '11px' }}>Version info unavailable.</p>;
  }
  const ver = verState.data;
  // changelog can be either a single string (current beta schema, multi-build
  // notes concatenated) OR an array of build-entry objects. The original code
  // unconditionally called .slice(0,3).map() and c.changes.map() which threw
  // a TypeError under both observed shapes. With no error boundary above, the
  // throw blanked the whole app — what coaches saw as "settings opens
  // momentarily then blank page." Render defensively for every observed and
  // plausibly-future shape.
  const changelogIsArray = Array.isArray(ver.changelog);
  const changelogText = typeof ver.changelog === 'string' ? ver.changelog : '';
  return (
    <div>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '12px', color: 'var(--text-secondary)' }}>
        <span>📦 v{safeText(ver.version)}</span>
        <span>🔧 Build #{safeText(ver.build)}</span>
        <span>📅 {safeText(ver.date)}</span>
      </div>
      {changelogIsArray && ver.changelog.length > 0 && (
        <div style={{ marginTop: '8px', maxHeight: '160px', overflowY: 'auto', fontSize: '11px', color: 'var(--text-dim)' }}>
          {ver.changelog.slice(0, 3).map((c, i) => (
            <div key={i} style={{ marginBottom: '6px' }}>
              <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Build #{safeText(c?.build)}</span>{' '}
              <span style={{ opacity: 0.6 }}>({safeText(c?.date)})</span>
              {Array.isArray(c?.changes) && c.changes.length > 0 && (
                <ul style={{ margin: '2px 0 0 16px', padding: 0 }}>
                  {c.changes.map((ch, j) => <li key={j}>{safeText(ch)}</li>)}
                </ul>
              )}
              {(() => {
                // Normalise: summary | note | changes-as-string all render the same way
                const label = typeof c?.summary === 'string' ? c.summary
                  : typeof c?.note === 'string' ? c.note
                  : !Array.isArray(c?.changes) && typeof c?.changes === 'string' ? c.changes
                  : null;
                return label ? (
                  <div style={{ marginLeft: '8px', marginTop: '2px', fontWeight: 500 }}>{label}</div>
                ) : null;
              })()}
              {Array.isArray(c?.details) && c.details.length > 0 && (
                <ul style={{ margin: '2px 0 0 16px', padding: 0, opacity: 0.85 }}>
                  {c.details.map((d, j) => <li key={j}>{safeText(d)}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
      {!changelogIsArray && changelogText && (
        <div style={{ marginTop: '8px', maxHeight: '160px', overflowY: 'auto', fontSize: '11px', color: 'var(--text-dim)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
          {changelogText}
        </div>
      )}
    </div>
  );
};

const SettingsPanel = ({ open, onClose, onLogout, onVisualSettingsChange, experienceMode = 'essentials', onExperienceModeChange, onProfileSettingsChange }) => {
  const user = getStoredUser();
  const mfaEligible = ['admin', 'club_admin', 'coach'].includes(user?.role);
  const [settings, setSettings] = useState({
    coachMode: 'strategy',
    activeStance: 'scout',
    appearance: localStorage.getItem('eliteedge_theme') || 'dark',
    fontFamily: localStorage.getItem('eliteedge_font') || 'Segoe UI',
    fontSize: localStorage.getItem('eliteedge_fontSize') || 'M',
    experienceMode: localStorage.getItem('eliteedge_experience_mode') || experienceMode,
    country: user?.country || 'AU',
    locale: user?.locale || 'en-AU',
    terminology: user?.terminology || 'auto',
    pacedReading: localStorage.getItem('eliteedge_pacedReading') === 'true',
    coachingPhilosophy: '',
    activeClub: '',
    activeProject: ''
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [pwMessage, setPwMessage] = useState({ type: '', text: '' });
  const [plan, setPlan] = useState(null);
  const [mfaInfo, setMfaInfo] = useState({ enabled: false, hasRecoveryCodes: false, recoveryCodesRemaining: 0 });
  const [mfaSetup, setMfaSetup] = useState({ manualEntryKey: '', otpauthUrl: '', recoveryCodes: [] });
  const [setupCode, setSetupCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disableRecoveryCode, setDisableRecoveryCode] = useState('');
  const [mfaMessage, setMfaMessage] = useState({ type: '', text: '' });
  const [mfaBusy, setMfaBusy] = useState(false);
  const [supportProfile, setSupportProfile] = useState({
    mobilePhone: user?.mobilePhone || '',
    representedClubName: user?.representedClubName || '',
    supportContactConsent: Boolean(user?.supportContactConsent),
    reminderEmailEnabled: user?.reminderEmailEnabled !== false,
  });
  const [supportProfileSaving, setSupportProfileSaving] = useState(false);
  const safeOtpAuthUrl = typeof mfaSetup.otpauthUrl === 'string' && /^otpauth:\/\/totp\//i.test(mfaSetup.otpauthUrl)
    ? mfaSetup.otpauthUrl
    : '';

  useEffect(() => {
    if (open) { fetchSettings(); fetchPlan(); }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setMfaSetup({ manualEntryKey: '', otpauthUrl: '', recoveryCodes: [] });
      setSetupCode('');
    }
  }, [open]);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/ai/settings');
      if (res.data?.data) setSettings(s => ({ ...s, ...res.data.data }));
    } catch (err) {
      console.error('Failed to load settings');
    }
  };

  const fetchPlan = async () => {
    try {
      const res = await api.get('/auth/me');
      const profile = res.data?.data || res.data?.user || res.data || {};
      const sub = profile.subscription;
      const mfa = res.data?.data?.mfa || res.data?.user?.mfa || res.data?.mfa || {};
      setPlan(sub ? { ...sub, effectivePlan: profile.effectivePlan || sub.plan } : null);
      const remaining = Number.isFinite(mfa.recoveryCodesRemaining) ? mfa.recoveryCodesRemaining
        : (Number.isFinite(mfa.remainingRecoveryCodes) ? mfa.remainingRecoveryCodes : 0);
      setMfaInfo({
        enabled: !!mfa.enabled,
        hasRecoveryCodes: !!mfa.hasRecoveryCodes || remaining > 0,
        recoveryCodesRemaining: remaining
      });
      setSupportProfile({
        mobilePhone: profile.mobilePhone || '',
        representedClubName: profile.representedClubName || '',
        supportContactConsent: Boolean(profile.supportContactConsent),
        reminderEmailEnabled: profile.reminderEmailEnabled !== false,
      });
    } catch {
      // Non-fatal — the plan section just stays hidden if /me is unavailable.
    }
  };

  const LOCAL_STORAGE_KEYS = {
    appearance: 'eliteedge_theme',
    fontFamily: 'eliteedge_font',
    fontSize: 'eliteedge_fontSize',
    pacedReading: 'eliteedge_pacedReading'
  };

  const saveSetting = async (key, value) => {
    setSettings(s => ({ ...s, [key]: value }));
    // Persist UI preferences to localStorage
    if (LOCAL_STORAGE_KEYS[key]) {
      localStorage.setItem(LOCAL_STORAGE_KEYS[key], value.toString());
    }
    if (key === 'appearance' || key === 'fontFamily' || key === 'fontSize') {
      onVisualSettingsChange?.({
        ...(key === 'appearance' ? { theme: value } : {}),
        ...(key === 'fontFamily' ? { font: value } : {}),
        ...(key === 'fontSize' ? { fontSize: value } : {}),
      });
    }
    if (key === 'experienceMode') {
      localStorage.setItem('eliteedge_experience_mode', value);
      onExperienceModeChange?.(value);
    }
    if (key === 'terminology') {
      onProfileSettingsChange?.({ terminology: value });
    }
    try {
      await api.put('/ai/settings', { [key]: value });
    } catch (err) {
      console.error('Failed to save setting');
    }
  };

  const saveSupportProfile = async () => {
    setSupportProfileSaving(true);
    setMessage('');
    try {
      const res = await authAPI.updateProfile(supportProfile);
      const nextUser = res.data?.user;
      if (nextUser) {
        localStorage.setItem('user', JSON.stringify(nextUser));
        onProfileSettingsChange?.(nextUser);
      }
      setMessage('Onboarding contact details saved');
    } catch (err) {
      setMessage(err.response?.data?.error || 'Could not save onboarding contact details');
    } finally {
      setSupportProfileSaving(false);
    }
  };

  const handleExportThread= async () => {
    try {
      const res = await api.get('/ai/conversations');
      const convos = res.data?.data || [];
      if (convos.length === 0) {
        setMessage('No conversations to export');
        setTimeout(() => setMessage(''), 2000);
        return;
      }
      const latest = convos[0];
      const fullRes = await api.get('/ai/conversations/' + latest._id);
      const convo = fullRes.data?.data;
      if (!convo || !convo.messages) {
        setMessage('Could not load conversation');
        setTimeout(() => setMessage(''), 2000);
        return;
      }
      let textContent = 'EliteEdge AI - Chat Export\n';
      textContent += '========================\n';
      textContent += 'Title: ' + (convo.title || 'Untitled') + '\n';
      textContent += 'Date: ' + new Date(convo.updatedAt || convo.createdAt).toLocaleString() + '\n';
      textContent += '========================\n\n';
      convo.messages.forEach(function(m) {
        var role = m.role === 'user' ? 'Coach' : 'AI Assistant';
        textContent += '[' + role + ']\n' + m.content + '\n\n';
      });
      var blob = new Blob([textContent], { type: 'text/plain' });
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'chat-export.txt';
      a.click();
      URL.revokeObjectURL(url);
      setMessage('Chat exported successfully');
      setTimeout(() => setMessage(''), 2000);
    } catch (err) {
      setMessage('Export failed');
      setTimeout(() => setMessage(''), 2000);
    }
  };

  const savePhilosophy = async () => {
    setSaving(true);
    try {
      await api.put('/ai/settings', { coachingPhilosophy: settings.coachingPhilosophy });
      if (settings.coachingPhilosophy.trim().length > 0) {
        await completeJourneyStep('foundation');
      }
      setMessage('Philosophy saved');
      setTimeout(() => setMessage(''), 2000);
    } catch (err) {
      setMessage('Failed to save');
    }
    setSaving(false);
  };

  const changePassword = async () => {
    if (passwords.new !== passwords.confirm) {
      setPwMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    const policyError = getPasswordPolicyError(passwords.new);
    if (policyError) {
      setPwMessage({ type: 'error', text: policyError });
      return;
    }

    try {
      await api.post('/ai/change-password', { currentPassword: passwords.current, newPassword: passwords.new });
      setPwMessage({ type: 'success', text: 'Password changed successfully!' });
      localStorage.removeItem('mustChangePassword');
      setPasswords({ current: '', new: '', confirm: '' });
      setTimeout(() => { setPwMessage({ type: '', text: '' }); }, 5000);
    } catch (err) {
      setPwMessage({ type: 'error', text: err.response?.data?.error || 'Failed to change password' });
    }
  };

  const startMfaSetup = async () => {
    setMfaBusy(true);
    setMfaMessage({ type: '', text: '' });
    setSetupCode('');
    try {
      const res = await authAPI.mfaSetup();
      const data = res.data || {};
      setMfaSetup({
        manualEntryKey: data.manualEntryKey || '',
        otpauthUrl: data.otpauthUrl || data.otpauth_url || '',
        recoveryCodes: Array.isArray(data.recoveryCodes) ? data.recoveryCodes : []
      });
      setMfaMessage({ type: 'success', text: 'Setup started. Scan the QR code below. If you cannot scan, use the manual key.' });
    } catch (err) {
      setMfaMessage({ type: 'error', text: err.response?.data?.error || 'Failed to start MFA setup' });
    }
    setMfaBusy(false);
  };

  const verifyMfaSetup = async () => {
    if (!setupCode.trim()) {
      setMfaMessage({ type: 'error', text: 'Enter a 6-digit authenticator code to verify setup.' });
      return;
    }
    setMfaBusy(true);
    setMfaMessage({ type: '', text: '' });
    try {
      const res = await authAPI.mfaVerifySetup(setupCode.trim());
      const codes = Array.isArray(res.data?.recoveryCodes) ? res.data.recoveryCodes : mfaSetup.recoveryCodes;
      setMfaSetup({ manualEntryKey: '', otpauthUrl: '', recoveryCodes: codes });
      setSetupCode('');
      setMfaMessage({ type: 'success', text: 'MFA enabled successfully. Save your recovery codes in a secure place.' });
      await fetchPlan();
    } catch (err) {
      setMfaMessage({ type: 'error', text: err.response?.data?.error || 'Failed to verify MFA setup' });
    }
    setMfaBusy(false);
  };

  const regenerateRecoveryCodes = async () => {
    if (!window.confirm('This will invalidate your previous recovery codes. Continue?')) return;
    setMfaBusy(true);
    setMfaMessage({ type: '', text: '' });
    try {
      const res = await authAPI.mfaRegenerateRecoveryCodes();
      const codes = Array.isArray(res.data?.recoveryCodes) ? res.data.recoveryCodes : [];
      setMfaSetup(prev => ({ ...prev, recoveryCodes: codes }));
      setMfaMessage({ type: 'success', text: 'Recovery codes regenerated. Save the new codes now; old ones no longer work.' });
      await fetchPlan();
    } catch (err) {
      setMfaMessage({ type: 'error', text: err.response?.data?.error || 'Failed to regenerate recovery codes' });
    }
    setMfaBusy(false);
  };

  const disableMfa = async () => {
    if (!disableCode.trim() && !disableRecoveryCode.trim()) {
      setMfaMessage({ type: 'error', text: 'Enter either an authenticator code or a recovery code to disable MFA.' });
      return;
    }
    if (!window.confirm('Disable MFA for this account? This lowers account security.')) return;
    setMfaBusy(true);
    setMfaMessage({ type: '', text: '' });
    try {
      await authAPI.mfaDisable(disableCode.trim() || undefined, disableRecoveryCode.trim() || undefined);
      setDisableCode('');
      setDisableRecoveryCode('');
      setMfaSetup({ manualEntryKey: '', otpauthUrl: '', recoveryCodes: [] });
      setMfaMessage({ type: 'success', text: 'MFA disabled for this account.' });
      await fetchPlan();
    } catch (err) {
      setMfaMessage({ type: 'error', text: err.response?.data?.error || 'Failed to disable MFA' });
    }
    setMfaBusy(false);
  };

  if (!open) return null;

  const sectionStyle = { marginBottom: '20px' };
  const labelStyle = { color: 'var(--accent)', fontSize: '11px', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '10px', display: 'block' };
  const rowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' };
  const textStyle = { color: 'var(--text-primary)', fontSize: '14px' };
  const dimStyle = { color: 'var(--text-dim)', fontSize: '12px' };
  const btnStyle = (active) => ({
    padding: '8px 20px', borderRadius: '20px', border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
    background: active ? 'var(--accent)' : 'var(--bg-input)', color: active ? 'var(--text-on-accent)' : 'var(--text-secondary)',
    transition: 'all 0.2s'
  });
  const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', background: 'var(--bg-sidebar)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', fontSize: '13px', boxSizing: 'border-box' };
  const selectStyle = { ...inputStyle, appearance: 'auto' };

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'var(--bg-primary)' }} />
      
      {/* Panel */}
      <div style={{
        position: 'relative', width: 'calc(100% - 16px)', maxWidth: '560px', maxHeight: '90vh', marginTop: '40px',
        background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border-color)',
        overflowY: 'auto', padding: '28px', color: 'var(--text-primary)', boxSizing: 'border-box'
      }}>
        {/* Handle bar */}
        <div style={{ width: '40px', height: '4px', background: 'var(--border-input)', borderRadius: '2px', margin: '0 auto 20px' }} />

        <h2 style={{ margin: '0 0 24px', fontSize: '20px', fontWeight: 700 }}>⚙ Settings</h2>

        {message && (
          <div style={{ background: 'var(--surface-soft)', border: '1px solid var(--accent)', color: 'var(--accent)', padding: '8px 12px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>{message}</div>
        )}

        {/* QUICK ACTIONS */}
        <div style={sectionStyle}>
          <span style={labelStyle}>QUICK ACTIONS</span>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleExportThread} style={{ ...btnStyle(false), background: 'var(--bg-input)', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>📄 Export Full Thread</button>
          </div>
        </div>

        {/* PLAN */}
        {plan && (() => {
          const currentPlan = plan.effectivePlan || plan.plan || 'free';
          const isActivePlan = ['essentials', 'pro', 'club'].includes(currentPlan);
          const end = plan.currentPeriodEnd || plan.validUntil;
          const endTime = end ? new Date(end).getTime() : null;
          const hasValidEnd = endTime != null && Number.isFinite(endTime);
          const active = isActivePlan && (!end || (hasValidEnd && endTime > Date.now()));
          const daysLeft = hasValidEnd ? Math.ceil((endTime - Date.now()) / 86400000) : null;
          const fmt = (d) => new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
          const planLabel = { free: 'Free', essentials: 'Coach Essentials', pro: 'Coach Pro', club: 'Club' };
          return (
            <div style={sectionStyle}>
              <span style={labelStyle}>PLAN</span>
              <div style={{ ...rowStyle, marginBottom: '4px' }}>
                <span style={textStyle}>Current plan</span>
                <span style={{
                  ...textStyle, fontWeight: 700,
                  color: active ? 'var(--accent)' : 'var(--text-dim)'
                }}>{active ? planLabel[currentPlan] : 'Free'}</span>
              </div>
              {active && hasValidEnd && (
                <div style={rowStyle}>
                  <span style={dimStyle}>{daysLeft != null && daysLeft <= 7 ? 'Expires soon' : 'Renews / valid until'}</span>
                  <span style={{ ...dimStyle, color: daysLeft != null && daysLeft <= 7 ? 'var(--warning)' : 'var(--text-dim)' }}>
                    {fmt(end)}{daysLeft != null ? ` · ${daysLeft}d left` : ''}
                  </span>
                </div>
              )}
              {active && !hasValidEnd && (
                <p style={{ ...dimStyle, margin: 0 }}>Active — no expiry.</p>
              )}
              {!active && (
                <p style={{ ...dimStyle, margin: 0 }}>You're on the Free plan.</p>
              )}
            </div>
          );
        })()}

        {/* MULTI-FACTOR AUTHENTICATION */}
        <div style={sectionStyle}>
          <span style={labelStyle}>MULTI-FACTOR AUTHENTICATION</span>
          <p style={{ ...dimStyle, margin: '0 0 10px 0' }}>
            MFA is optional. You can enable it for your own account without affecting other users.
          </p>
          {!mfaEligible && (
            <p style={{ ...dimStyle, margin: '0 0 10px 0' }}>
              MFA setup is currently limited to admin, club-admin, and coach accounts.
            </p>
          )}
          <div style={{ ...rowStyle, marginBottom: '8px' }}>
            <span style={textStyle}>Status</span>
            <span style={{ ...dimStyle, color: mfaInfo.enabled ? 'var(--success)' : 'var(--text-dim)', fontWeight: 600 }}>
              {mfaInfo.enabled ? 'Enabled' : 'Not enabled'}
            </span>
          </div>
          {mfaInfo.enabled && (
            <div style={{ ...rowStyle, marginBottom: '10px' }}>
              <span style={textStyle}>Recovery codes remaining</span>
              <span style={dimStyle}>{mfaInfo.recoveryCodesRemaining}</span>
            </div>
          )}
          <div role="status" aria-live="polite" aria-atomic="true" style={{ marginBottom: mfaMessage.text ? '10px' : 0, padding: mfaMessage.text ? '8px 10px' : 0, borderRadius: '8px', border: mfaMessage.text ? `1px solid ${mfaMessage.type === 'error' ? 'var(--danger)' : 'var(--success)'}` : 'none', color: mfaMessage.type === 'error' ? 'var(--danger)' : 'var(--accent)', background: mfaMessage.text ? (mfaMessage.type === 'error' ? 'var(--surface-soft)' : 'var(--surface-soft)') : 'transparent', fontSize: '12px' }}>
            {mfaMessage.text || ''}
          </div>

          {!mfaInfo.enabled && mfaEligible ? (
            <>
              <button onClick={startMfaSetup} disabled={mfaBusy} aria-busy={mfaBusy} style={{ ...btnStyle(true), borderRadius: '8px', width: '100%', marginBottom: '10px' }}>
                {mfaBusy ? 'Starting setup...' : 'Enable MFA'}
              </button>
              {(mfaSetup.manualEntryKey || safeOtpAuthUrl) && (
                <section aria-labelledby="mfa-setup-heading" style={{ background: 'var(--bg-sidebar)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px' }}>
                  <h3 id="mfa-setup-heading" style={{ ...textStyle, fontSize: '13px', margin: '0 0 8px 0' }}>Authenticator setup</h3>

                  {safeOtpAuthUrl && (
                    <figure role="img" aria-label="MFA QR code for authenticator app setup" style={{ margin: '0 0 10px 0', textAlign: 'center' }}>
                      <p style={{ ...dimStyle, margin: '0 0 6px 0' }}>Scan this QR code in your authenticator app:</p>
                      <div style={{ display: 'inline-block', background: 'var(--bg-input)', padding: '10px', borderRadius: '8px' }}>
                        <QRCodeSVG
                          value={safeOtpAuthUrl}
                          size={180}
                          includeMargin
                          title="MFA setup QR code"
                          bgColor="var(--bg-input)"
                          fgColor="var(--text-primary)"
                        />
                      </div>
                    </figure>
                  )}

                  {mfaSetup.manualEntryKey && (
                    <>
                      <p style={{ ...dimStyle, margin: '0 0 6px 0' }}>Can’t scan? Enter this setup key manually:</p>
                      <code style={{ display: 'block', fontSize: '12px', color: 'var(--text-primary)', wordBreak: 'break-all', marginBottom: '10px' }}>{mfaSetup.manualEntryKey}</code>
                    </>
                  )}

                  <label htmlFor="mfa-setup-code" style={{ ...dimStyle, display: 'block', marginBottom: '4px' }}>Verify with a 6-digit code</label>
                  <input id="mfa-setup-code" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={setupCode} onChange={e => setSetupCode(e.target.value)} placeholder="000000" style={{ ...inputStyle, marginBottom: '8px' }} />
                  <button onClick={verifyMfaSetup} disabled={mfaBusy || !setupCode.trim()} style={{ ...btnStyle(true), borderRadius: '8px', width: '100%' }}>
                    {mfaBusy ? 'Verifying...' : 'Verify and turn on MFA'}
                  </button>
                </section>
              )}
            </>
          ) : mfaEligible ? (
            <>
              <button onClick={regenerateRecoveryCodes} disabled={mfaBusy} style={{ ...btnStyle(false), width: '100%', borderRadius: '8px', marginBottom: '10px' }}>
                Regenerate recovery codes
              </button>
              <div style={{ background: 'var(--bg-sidebar)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px' }}>
                <p style={{ ...dimStyle, margin: '0 0 6px 0' }}>Disable MFA (use authenticator OR recovery code)</p>
                <label htmlFor="mfa-disable-code" style={{ ...dimStyle, display: 'block', marginBottom: '4px' }}>Authenticator code</label>
                <input id="mfa-disable-code" type="text" inputMode="numeric" maxLength={6} value={disableCode} onChange={e => setDisableCode(e.target.value)} placeholder="000000" aria-label="Authenticator code to disable MFA" style={{ ...inputStyle, marginBottom: '8px' }} />
                <label htmlFor="mfa-disable-recovery" style={{ ...dimStyle, display: 'block', marginBottom: '4px' }}>Recovery code</label>
                <input id="mfa-disable-recovery" type="text" value={disableRecoveryCode} onChange={e => setDisableRecoveryCode(e.target.value)} placeholder="XXXXXX-XXXXXX" aria-label="Recovery code to disable MFA" style={{ ...inputStyle, marginBottom: '8px' }} />
                <button onClick={disableMfa} disabled={mfaBusy || (!disableCode.trim() && !disableRecoveryCode.trim())} style={{ ...btnStyle(false), width: '100%', borderRadius: '8px', border: '1px solid var(--danger)', color: 'var(--danger)' }}>
                  {mfaBusy ? 'Disabling...' : 'Disable MFA'}
                </button>
              </div>
            </>
          ) : null}

          {Array.isArray(mfaSetup.recoveryCodes) && mfaSetup.recoveryCodes.length > 0 && (
            <div style={{ marginTop: '10px', background: 'var(--bg-sidebar)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px' }}>
              <p style={{ ...textStyle, margin: '0 0 6px 0', fontWeight: 600 }}>Recovery codes (save these now)</p>
              <p style={{ ...dimStyle, margin: '0 0 8px 0' }}>Each code can be used once. Old codes are invalid after regeneration.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                {mfaSetup.recoveryCodes.map((code) => (
                  <code key={code} style={{ display: 'block', fontSize: '12px', color: 'var(--text-primary)', background: 'var(--bg-input)', borderRadius: '6px', padding: '6px 8px' }}>{code}</code>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* COACH MODE */}
        <div style={sectionStyle}>
          <span style={labelStyle}>COACH MODE</span>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '6px' }}>
            <button onClick={() => saveSetting('coachMode', 'strategy')} style={btnStyle(settings.coachMode === 'strategy')}>⚡ Strategy</button>
            <button onClick={() => saveSetting('coachMode', 'study')} style={btnStyle(settings.coachMode === 'study')}>📚 Study</button>
          </div>
          <p style={dimStyle}>Strategy = quick tactical talk · Study = deep document analysis + reports</p>
        </div>

        {/* ACTIVE STANCE */}
        <div style={sectionStyle}>
          <span style={labelStyle}>ACTIVE STANCE</span>
          <p style={{ ...dimStyle, marginBottom: '8px' }}>Coach AI automatically senses your question type and adapts its focus.</p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {['scout', 'tactician', 'trainer', 'developer'].map(stance => (
              <button key={stance} onClick={() => saveSetting('activeStance', stance)}
                style={{ ...btnStyle(settings.activeStance === stance), textTransform: 'capitalize', fontSize: '12px', padding: '6px 14px' }}>
                🔍 {stance}
              </button>
            ))}
          </div>
        </div>

        {/* APPEARANCE */}
        <div style={sectionStyle}>
          <span style={labelStyle}>APPEARANCE</span>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '6px' }}>
            <button onClick={() => saveSetting('appearance', 'dark')} style={btnStyle(settings.appearance === 'dark')}>🌙 Dark</button>
            <button onClick={() => saveSetting('appearance', 'light')} style={btnStyle(settings.appearance === 'light')}>☀️ Light</button>
          </div>
          <p style={dimStyle}>Dark = Hawk-Eye field · Light = daytime readability</p>
        </div>

        {/* TYPOGRAPHY */}
        <div style={sectionStyle}>
          <span style={labelStyle}>TYPOGRAPHY</span>
          <div style={rowStyle}>
            <span style={textStyle}>Font Family</span>
            <select value={settings.fontFamily} onChange={e => saveSetting('fontFamily', e.target.value)} aria-label="Font family"
              style={{ ...selectStyle, width: 'auto', minWidth: '160px' }}>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Inter">Inter</option>
              <option value="Roboto">Roboto</option>
              <option value="Georgia">Georgia</option>
              <option value="monospace">Monospace</option>
            </select>
          </div>
          <div style={rowStyle}>
            <span style={textStyle}>Font Size</span>
            <div style={{ display: 'flex', gap: '6px' }}>
              {['S', 'M', 'L'].map(size => (
                <button key={size} onClick={() => saveSetting('fontSize', size)}
                  style={{
                    width: '36px', height: '36px', borderRadius: '8px', border: 'none', fontWeight: 700, cursor: 'pointer',
                    background: settings.fontSize === size ? 'var(--accent)' : 'var(--bg-input)',
                    color: settings.fontSize === size ? 'var(--text-primary)' : 'var(--text-secondary)'
                  }}>{size}</button>
              ))}
            </div>
          </div>

          {/* COACH EXPERIENCE */}
          <div style={sectionStyle}>
            <span style={labelStyle}>COACH EXPERIENCE</span>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '6px' }}>
              <button
                onClick={() => saveSetting('experienceMode', 'essentials')}
                style={btnStyle((settings.experienceMode || experienceMode) !== 'full')}
              >
                📋 Essentials
              </button>
              <button
                onClick={() => saveSetting('experienceMode', 'full')}
                style={btnStyle((settings.experienceMode || experienceMode) === 'full')}
              >
                🔓 Full
              </button>
            </div>
            <p style={dimStyle}>Essentials keeps the everyday coaching workflow focused. Full shows every feature included in your plan.</p>
          </div>

          {/* LANGUAGE AND TERMINOLOGY */}
          <div style={sectionStyle}>
            <span style={labelStyle}>REGION & TERMINOLOGY</span>
            <div style={rowStyle}>
              <span style={textStyle}>Region</span>
              <select value={settings.country || 'AU'} onChange={e => {
                const nextCountry = e.target.value;
                const nextLocale = nextCountry === 'US' ? 'en-US' : 'en-AU';
                setSettings(s => ({ ...s, country: nextCountry, locale: nextLocale }));
                onProfileSettingsChange?.({ country: nextCountry, locale: nextLocale });
                api.put('/ai/settings', { country: nextCountry, locale: nextLocale }).catch(() => {});
              }} aria-label="Region" style={{ ...selectStyle, width: 'auto', minWidth: '160px' }}>
                <option value="AU">Australia</option>
                <option value="US">United States</option>
                <option value="OTHER">Other (Australian English)</option>
              </select>
            </div>
            <div style={rowStyle}>
              <span style={textStyle}>Sport wording</span>
              <select value={settings.terminology || 'auto'} onChange={e => saveSetting('terminology', e.target.value)} aria-label="Sport terminology" style={{ ...selectStyle, width: 'auto', minWidth: '160px' }}>
                <option value="auto">Automatic</option>
                <option value="football">Football</option>
                <option value="soccer">Soccer</option>
              </select>
            </div>
            <p style={dimStyle}>Australia uses training, match and squad. United States uses practice, game and roster. Times always follow this device.</p>
          </div>
        </div>

        {/* READING EXPERIENCE */}
        <div style={sectionStyle}>
          <span style={labelStyle}>READING EXPERIENCE</span>
          <div style={rowStyle}>
            <div>
              <p style={{ ...textStyle, margin: 0, fontWeight: 600 }}>Paced Reading</p>
              <p style={{ ...dimStyle, margin: 0 }}>Pause between sections — tap to continue for long AI responses</p>
            </div>
            <div onClick={() => saveSetting('pacedReading', !settings.pacedReading)}
              role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); saveSetting('pacedReading', !settings.pacedReading); } }}
              style={{ width: '20px', height: '20px', borderRadius: '50%', border: '2px solid var(--border-input)', background: settings.pacedReading ? 'var(--accent)' : 'transparent', cursor: 'pointer' }} />
          </div>
        </div>

        {/* COACHING PHILOSOPHY */}
        <div style={sectionStyle}>
          <span style={labelStyle}>CLUB COACHING PHILOSOPHY</span>
          <p style={{ color: 'var(--text-dim)', fontSize: '11px', margin: '0 0 6px 0' }}>This applies across all your teams. Set team-specific philosophy in team management.</p>
          <textarea value={settings.coachingPhilosophy} onChange={e => setSettings(s => ({ ...s, coachingPhilosophy: e.target.value }))} aria-label="Club coaching philosophy"
            placeholder="e.g. Possession-based football with high pressing and player development focus..."
            rows={3} style={{ ...inputStyle, marginBottom: '8px' }} />
          <button onClick={savePhilosophy} disabled={saving}
            style={{ ...btnStyle(true), borderRadius: '8px' }}>
            {saving ? 'Saving...' : 'Save Club Philosophy'}
          </button>
        </div>

        {/* CLUB INFO */}
        <div style={sectionStyle}>
          <span style={labelStyle}>NOTIFICATIONS</span>
          <NotificationSettings />
        </div>

        {/* CLUB INFO */}
        {user?.role !== 'parent' && (
          <div style={sectionStyle}>
            <span style={labelStyle}>ONBOARDING CONTACT</span>
            <p style={{ ...dimStyle, margin: '0 0 10px' }}>
              Optional support details only. The organisation below does not create a club or affect club membership or search.
            </p>
            <label htmlFor="support-mobile-phone" style={{ ...dimStyle, display: 'block', marginBottom: '4px' }}>Mobile number</label>
            <input
              id="support-mobile-phone"
              type="tel"
              autoComplete="tel"
              maxLength={32}
              value={supportProfile.mobilePhone}
              onChange={e => setSupportProfile(p => ({ ...p, mobilePhone: e.target.value }))}
              placeholder="Optional"
              style={{ ...inputStyle, marginBottom: '10px' }}
            />
            <label htmlFor="support-represented-club" style={{ ...dimStyle, display: 'block', marginBottom: '4px' }}>Club or organisation represented</label>
            <input
              id="support-represented-club"
              type="text"
              maxLength={160}
              value={supportProfile.representedClubName}
              onChange={e => setSupportProfile(p => ({ ...p, representedClubName: e.target.value }))}
              placeholder="Optional"
              style={{ ...inputStyle, marginBottom: '10px' }}
            />
            {supportProfile.mobilePhone.trim() && (
              <label style={{ ...dimStyle, display: 'block', marginBottom: '10px', lineHeight: 1.4 }}>
                <input
                  type="checkbox"
                  checked={supportProfile.supportContactConsent}
                  onChange={e => setSupportProfile(p => ({ ...p, supportContactConsent: e.target.checked }))}
                  style={{ marginRight: '8px' }}
                />
                EliteEdge may contact me by phone about completing account setup.
              </label>
            )}
            <label style={{ ...dimStyle, display: 'block', marginBottom: '10px', lineHeight: 1.4 }}>
              <input
                type="checkbox"
                checked={supportProfile.reminderEmailEnabled}
                onChange={e => setSupportProfile(p => ({ ...p, reminderEmailEnabled: e.target.checked }))}
                style={{ marginRight: '8px' }}
              />
              Email me reminders if club setup is incomplete.
            </label>
            <button onClick={saveSupportProfile} disabled={supportProfileSaving} style={{ ...btnStyle(true), borderRadius: '8px' }}>
              {supportProfileSaving ? 'Saving...' : 'Save Contact Details'}
            </button>
          </div>
        )}

        {/* CLUB INFO */}
        <div style={sectionStyle}>
          <span style={labelStyle}>📁 CLUB</span>
          <div style={rowStyle}>
            <span style={textStyle}>Active Club</span>
            <span style={{ background: 'var(--bg-input)', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', color: 'var(--accent)' }}>
              {settings.activeClub || user?.clubName || 'Not set'}
            </span>
          </div>
        </div>

        {/* USER INFO */}
        <div style={sectionStyle}>
          <span style={labelStyle}>USER INFO</span>
          {[
            ['Environment', window.location.hostname.includes('localhost') ? 'Development' : 'Production'],
            ['User', user?.name || user?.email || '—'],
            ['Role', (user?.role || 'coach').charAt(0).toUpperCase() + (user?.role || 'coach').slice(1)]
          ].map(([k, v]) => (
            <div key={k} style={rowStyle}>
              <span style={textStyle}>{k}</span>
              <span style={dimStyle}>{v}</span>
            </div>
          ))}
        </div>

        {/* PASSWORD CHANGE */}
        {showPasswordChange && (
          <div style={{ ...sectionStyle, background: 'var(--bg-sidebar)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
            <span style={labelStyle}>CHANGE PASSWORD</span>
            {pwMessage.text && <p style={{ color: pwMessage.type === 'success' ? 'var(--accent)' : 'var(--danger)', fontSize: '12px', marginBottom: '8px' }}>{pwMessage.text}</p>}
            <input type="password" aria-label="Current password" placeholder="Current password" value={passwords.current} onChange={e => setPasswords(p => ({ ...p, current: e.target.value }))} style={{ ...inputStyle, marginBottom: '8px' }} />
            <input type="password" aria-label="New password" placeholder="New password" value={passwords.new} onChange={e => setPasswords(p => ({ ...p, new: e.target.value }))} style={{ ...inputStyle, marginBottom: '8px' }} />
            <input type="password" aria-label="Confirm new password" placeholder="Confirm new password" value={passwords.confirm} onChange={e => setPasswords(p => ({ ...p, confirm: e.target.value }))} style={{ ...inputStyle, marginBottom: '4px' }} />
            <p style={{ color: 'var(--text-dim)', fontSize: '11px', marginBottom: '12px', margin: '0 0 12px 0' }}>{PASSWORD_POLICY_TEXT}</p>
            <button onClick={changePassword} style={{ ...btnStyle(true), borderRadius: '8px', width: '100%' }}>Update Password</button>
          </div>
        )}

        {/* DATA & PRIVACY */}
        <div style={sectionStyle}>
          <span style={labelStyle}>DATA & PRIVACY</span>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={async () => {
              try {
                const res = await api.get('/auth/export-data');
                const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                // eslint-disable-next-line no-restricted-syntax -- R227: download filename slug, not user-visible date label
                a.href = url; a.download = `eliteedge-data-export-${formatDayInZone(new Date())}.json`; a.click();
                URL.revokeObjectURL(url);
              } catch { alert('Failed to export data'); }
            }} style={{ ...btnStyle(true), borderRadius: '8px' }}>
              📥 Export All My Data
            </button>
            <button onClick={async () => {
              if (!window.confirm('⚠️ This will permanently delete your account and ALL data (videos, documents, chats, players). This cannot be undone. Are you sure?')) return;
              const confirmation = prompt('Type "DELETE MY ACCOUNT" to confirm:');
              if (confirmation !== 'DELETE MY ACCOUNT') { alert('Deletion cancelled.'); return; }
              try {
                const res = await api.delete('/auth/delete-account', { data: { confirmation } });
                const report = res.data?.deletionReport;
                if (report) {
                  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `eliteedge-account-deletion-report-${formatDayInZone(new Date())}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                }
                alert('Your account has been deleted. A deletion report has been downloaded.');
                localStorage.clear();
                window.location.href = '/';
              } catch { alert('Failed to delete account'); }
            }} style={{ padding: '8px 16px', background: 'var(--danger)', color: 'var(--text-on-accent)', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', fontWeight: 600 }}>
              🗑️ Delete My Account
            </button>
          </div>
          <p style={{ color: 'var(--text-dim)', fontSize: '10px', marginTop: '6px' }}>Export downloads all your data as JSON. Deletion is permanent and cannot be undone.</p>
        </div>

        {/* VERSION INFO */}
        <div style={sectionStyle}>
          <span style={labelStyle}>VERSION INFO</span>
          <VersionInfo />
        </div>

        {/* ACTION BUTTONS */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
          <button onClick={() => setShowPasswordChange(!showPasswordChange)}
            style={{ padding: '10px 16px', background: 'transparent', border: '1px solid var(--border-input)', color: 'var(--text-secondary)', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
            Change Password
          </button>
          <button onClick={onLogout}
            style={{ padding: '10px 16px', background: 'transparent', border: '1px solid var(--danger)', color: 'var(--danger)', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
            Sign Out
          </button>
          <button onClick={onClose}
            style={{ padding: '10px 20px', background: 'var(--accent)', border: 'none', color: 'var(--text-primary)', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
